// store/slices/userSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import userService from "../../services/userService";

interface UserProfile {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  xrp_balance: number;
  kyc_status: string | null;
  role: string;
  email_verified: boolean;
  is_active: boolean;
  created_at: string;
  country?: string | null;
}

interface UserState {
  profile: UserProfile | null;
  kycStatus: string | null;
  isLoading: boolean;
  isSubmittingKYC: boolean;
  isUploadingKYC: boolean;
  uploadProgress: number;
  error: string | null;
  successMessage: string | null;
}

const initialState: UserState = {
  profile: null,
  kycStatus: null,
  isLoading: false,
  isSubmittingKYC: false,
  isUploadingKYC: false,
  uploadProgress: 0,
  error: null,
  successMessage: null,
};

export const getProfile = createAsyncThunk(
  "user/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getProfile();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch profile");
    }
  },
);

export const submitKYC = createAsyncThunk(
  "user/submitKYC",
  async (formData: FormData, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setUploadProgress(0));
      dispatch(setIsUploadingKYC(true));
      const response = await userService.submitKYC(formData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to submit KYC documents");
    } finally {
      dispatch(setIsUploadingKYC(false));
      dispatch(setUploadProgress(0));
    }
  },
);

export const getKYCStatus = createAsyncThunk(
  "user/getKYCStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getKYCStatus();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch KYC status");
    }
  },
);

export const updateProfile = createAsyncThunk(
  "user/updateProfile",
  async (
    profileData: { first_name: string; last_name: string; country?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await userService.updateProfile(profileData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update profile");
    }
  },
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUserError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetUserState: () => initialState,
    setKYCStatus: (state, action: PayloadAction<string | null>) => {
      state.kycStatus = action.payload;
      if (state.profile) state.profile.kyc_status = action.payload;
    },
    setIsUploadingKYC: (state, action: PayloadAction<boolean>) => {
      state.isUploadingKYC = action.payload;
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(getProfile.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.profile = action.payload;
        const kycStatus = action.payload.kyc_status;
        state.kycStatus = kycStatus ?? null;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(submitKYC.pending, (state) => {
        state.isSubmittingKYC = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(submitKYC.fulfilled, (state, action: PayloadAction<any>) => {
        state.isSubmittingKYC = false;
        state.successMessage =
          action.payload.message || "KYC submitted successfully";
        state.kycStatus = "pending";
        if (state.profile) state.profile.kyc_status = "pending";
      })
      .addCase(submitKYC.rejected, (state, action) => {
        state.isSubmittingKYC = false;
        state.error = action.payload as string;
      })

      .addCase(getKYCStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getKYCStatus.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        if (action.payload.kyc_status) {
          state.kycStatus = action.payload.kyc_status;
          if (state.profile)
            state.profile.kyc_status = action.payload.kyc_status;
        }
      })
      .addCase(getKYCStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateProfile.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.successMessage =
          action.payload.message || "Profile updated successfully";
        // Merge returned user data back into profile, preserving country
        if (action.payload.user) {
          state.profile = {
            ...state.profile,
            ...action.payload.user,
          } as UserProfile;
        }
        state.kycStatus = action.payload.user?.kyc_status || null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearUserError,
  clearSuccessMessage,
  resetUserState,
  setKYCStatus,
  setIsUploadingKYC,
  setUploadProgress,
} = userSlice.actions;

export default userSlice.reducer;
